"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createDraftStore = createDraftStore;
exports.default = void 0;
exports.draftKey = draftKey;
exports.userIdFromKey = userIdFromKey;
/**
 * Local recovery drafts.
 *
 * When the idle warning fires, every panel holding unsaved work writes a copy
 * here. This is the save that always works: no network, no token, no compile
 * step, nothing that can be refused. A cloud save may also run, and it may
 * fail. This one does not.
 *
 * ── The rule this exists to enforce ──
 *
 * A sign-out is a security measure. It is not a reason to throw away an
 * afternoon of survey design.
 *
 * ── Why drafts are keyed by user ──
 *
 * Two people can share a machine. A draft written by one of them must never be
 * offered to the other. So the user id is part of every key, and signing in
 * clears every draft that belongs to somebody else.
 *
 * ── What this deliberately does not do ──
 *
 * It never restores anything on its own. Silently reinstating an edit somebody
 * deliberately abandoned is its own kind of data loss, and it is worse because
 * nobody can see where the change came from. The app reads the draft, offers
 * it, and does what the person says.
 */

const KEY_PREFIX = 'samsurveyor.draft.v1';
const SEPARATOR = '|';

/** Storage that quietly does nothing, for private browsing and for tests. */
const nullStorage = {
  length: 0,
  key: () => null,
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined
};
function defaultStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (err) {
    // Some browsers throw on the property access itself when storage is
    // blocked, rather than on the first read.
  }
  return nullStorage;
}

/** Keys must not contain the separator, or two different drafts could collide. */
function safePart(value) {
  return String(value === undefined || value === null ? '' : value).replace(/\|/g, '/');
}
function draftKey({
  userId,
  scope,
  key
}) {
  return [KEY_PREFIX, safePart(userId), safePart(scope), safePart(key)].join(SEPARATOR);
}

/** Pull the user id back out of a stored key, or '' if it is not one of ours. */
function userIdFromKey(storageKey) {
  const text = String(storageKey || '');
  if (!text.startsWith(`${KEY_PREFIX}${SEPARATOR}`)) return '';
  return text.split(SEPARATOR)[1] || '';
}

/**
 * Build a draft store.
 *
 * @param {object}   [options]
 * @param {Storage}  [options.storage]  defaults to window.localStorage.
 * @param {function} [options.now]      returns the current epoch ms.
 */
function createDraftStore(options = {}) {
  const storage = options.storage || defaultStorage();
  const now = options.now || (() => Date.now());

  /** Every one of our keys currently in storage. */
  function ourKeys() {
    const found = [];
    let count = 0;
    try {
      count = storage.length || 0;
    } catch (err) {
      return found;
    }
    for (let i = 0; i < count; i += 1) {
      let name = null;
      try {
        name = storage.key(i);
      } catch (err) {
        continue;
      }
      if (name && String(name).startsWith(`${KEY_PREFIX}${SEPARATOR}`)) found.push(name);
    }
    return found;
  }

  /**
   * Write a draft.
   *
   * Returns true if it was stored. Returns false if storage refused it, which
   * happens in private browsing and when the quota is full. A refused write
   * must never break the sign-out, so this reports rather than throws.
   */
  function saveDraft({
    userId,
    scope,
    key,
    payload,
    basedOnVersion = null,
    label = ''
  }) {
    if (!userId || !scope) return false;
    const record = {
      userId: String(userId),
      scope: String(scope),
      key: String(key === undefined || key === null ? '' : key),
      savedAt: now(),
      basedOnVersion,
      label,
      payload
    };
    try {
      storage.setItem(draftKey({
        userId,
        scope,
        key
      }), JSON.stringify(record));
      return true;
    } catch (err) {
      return false;
    }
  }

  /** Read one draft back, or null when there is none we can trust. */
  function readDraft({
    userId,
    scope,
    key
  }) {
    if (!userId || !scope) return null;
    let raw = null;
    try {
      raw = storage.getItem(draftKey({
        userId,
        scope,
        key
      }));
    } catch (err) {
      return null;
    }
    if (!raw) return null;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      // Corrupt. Treat it as absent rather than handing back rubbish that
      // would then be loaded into an editor.
      return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    // A record whose user does not match its key has been tampered with or
    // half-written. Do not offer it to anybody.
    if (String(parsed.userId || '') !== String(userId)) return null;
    return parsed;
  }
  function clearDraft({
    userId,
    scope,
    key
  }) {
    try {
      storage.removeItem(draftKey({
        userId,
        scope,
        key
      }));
    } catch (err) {
      // Nothing to do. A draft we cannot delete is not worth failing over.
    }
  }

  /** Every draft belonging to this user, newest first. */
  function listDrafts(userId) {
    const wanted = safePart(userId);
    const out = [];
    for (const name of ourKeys()) {
      if (userIdFromKey(name) !== wanted) continue;
      let raw = null;
      try {
        raw = storage.getItem(name);
      } catch (err) {
        continue;
      }
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') out.push(parsed);
      } catch (err) {
        // Skip a corrupt record rather than failing the whole list.
      }
    }
    return out.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  }

  /**
   * Drop every draft that does not belong to this user.
   *
   * Call this when somebody signs in. Two people share a laptop, and one of
   * them must never be shown the other's unsaved survey.
   *
   * @returns {number} how many were removed.
   */
  function clearDraftsForOtherUsers(currentUserId) {
    const keep = safePart(currentUserId);
    let removed = 0;
    for (const name of ourKeys()) {
      if (userIdFromKey(name) === keep) continue;
      try {
        storage.removeItem(name);
        removed += 1;
      } catch (err) {
        // Keep going. One stubborn key must not stop the rest.
      }
    }
    return removed;
  }

  /** Drop every draft we hold, for anybody. Used when clearing a browser. */
  function clearAllDrafts() {
    let removed = 0;
    for (const name of ourKeys()) {
      try {
        storage.removeItem(name);
        removed += 1;
      } catch (err) {
        // As above.
      }
    }
    return removed;
  }
  return {
    saveDraft,
    readDraft,
    clearDraft,
    listDrafts,
    clearDraftsForOtherUsers,
    clearAllDrafts
  };
}
var _default = exports.default = createDraftStore;